import { Dialog, DialogContent, DialogTitle, TextField, DialogActions, Button, Stack, Typography, Box, Grid, FormControl, Divider, Switch, Alert } from "@mui/material";
import { ICarrinhoItem } from "..";
import { useCallback, useMemo, useState } from "react";
import { formatarValorMonetario, formatarInputValorMonetário } from "../../../../../../../helpers/masks";
import { desformatarValorMonetario } from "../../../../../../../helpers/unmask";
import { QuantityInput } from "../../../../../../../components/BaseInputs/QuantityInput";
import { enqueueSnackbar } from "notistack";
import PaperDraggable from "../../../../../../../components/PaperDraggable";

export default function DialogEditItem({
    open,
    onClose,
    item,
    onEditarItem
}: DialogEditItemProps) {
    const [quantidade, setQuantidade] = useState<string>(item.quantidade.toString());
    const [descontoDisplay, setDescontoDisplay] = useState<string>(() => {
        const initialDiscount = item.tipoDesconto === "%" ? Number(((item.desconto / item.preco) * 100).toFixed(2)) : item.desconto;
        return item.tipoDesconto === "R$" ? formatarInputValorMonetário((initialDiscount * 100).toString()) : initialDiscount.toString();
    });

    const permiteDesconto = useMemo(() => {
        return !(
            item.loja.promocao ||
            !item.loja.parametros.permite_desconto
        );
    }, [
        item.loja.parametros.permite_desconto,
        item.loja.promocao,
    ]);

    const calcularDescontoMaximo = useMemo(() => {
        const { parametros } = item.loja;
        const descontoMaximoLoja = parametros.percentual_desconto_maximo;
        const descontoMaximoSetor = item.setor?.percentual_desconto_maximo;

        const deveUsarDescontoSetor = (descontoMaximoLoja === 100 && descontoMaximoSetor !== 100) && item.setor?.permite_desconto === "S";

        return deveUsarDescontoSetor ? descontoMaximoSetor : descontoMaximoLoja;
    }, [item.setor?.permite_desconto, item.setor?.percentual_desconto_maximo, item.loja]);

    const [valueForm, setValueForm] = useState<ICarrinhoItem>({
        id: item.id,
        nome: item.nome,
        quantidade: item.quantidade,
        desconto: item.tipoDesconto === "%" ? Number(((item.desconto / item.preco) * 100).toFixed(2)) : item.desconto,
        tipoDesconto: item.tipoDesconto,
        setor: item.setor,
        preco: item.preco,
        loja: item.loja,
        imagem: item.imagem,
        emite_receita: item.emite_receita,
        agrotoxico_id: item.agrotoxico_id,
        praga: item.praga,
        cultura: item.cultura
    })

    //Funções Manipuladoras
    const handleChange = useCallback((prop: "tipoDesconto" | "quantidade" | "desconto", value: any) => {
        setValueForm(prevState => {
            let newValue = value;
            let desconto = 0;

            if (prop === "tipoDesconto") {
                if (value === "%" && prevState.tipoDesconto === "R$") {
                    const percentual = (prevState.desconto / prevState.preco) * 100;
                    desconto = Math.floor(percentual * 100) / 100;
                    setDescontoDisplay(desconto.toString());
                } else if (value === "R$" && prevState.tipoDesconto === "%") {
                    const valorReais = (prevState.desconto * prevState.preco) / 100;
                    const temMilesimos = (valorReais * 1000) % 10 !== 0;
                    desconto = Math.floor(valorReais * 100) / 100;
                    if (temMilesimos || desconto % 1 !== 0) {
                        setDescontoDisplay(`R$ ${desconto.toString().replace('.', ',')}`);
                    } else {
                        setDescontoDisplay(formatarInputValorMonetário((desconto * 100).toString()));
                    }
                }

                return {
                    ...prevState,
                    [prop]: value,
                    desconto: desconto
                };
            }

            if (prop === "desconto") {
                if (prevState.tipoDesconto === "R$") {
                    const formattedValue = formatarInputValorMonetário(value);
                    setDescontoDisplay(formattedValue);
                    const numericValue = desformatarValorMonetario(formattedValue);
                    newValue = numericValue;
                } else {
                    setDescontoDisplay(value);
                    newValue = Number(value);
                }
            }

            return {
                ...prevState,
                [prop]: newValue
            };
        });
    }, []);

    const handleVerificarLimiteDesconto = useCallback(() => {
        const {
            preco,
            desconto,
            tipoDesconto
        } = valueForm

        const descontoFormatado = Number(desconto)
        const percentualDescontoMaximo = Number(calcularDescontoMaximo);
        const descontoLimite = (Number(preco) * percentualDescontoMaximo) / 100

        if (tipoDesconto === "%") {
            const valorDescontoEmReais = (Number(preco) * descontoFormatado) / 100;
            return valorDescontoEmReais > descontoLimite;
        }

        if (tipoDesconto === "R$") {
            return descontoFormatado > descontoLimite;
        }
    }, [valueForm, calcularDescontoMaximo])

    const handleConfirmValues = useCallback(() => {
        const { loja, preco, tipoDesconto } = valueForm
        const descontoFormatado = Number(valueForm.desconto)
        let valorDescontoEmReais = descontoFormatado

        if (tipoDesconto === "%") {
            const valorReais = ((Number(preco) * valorDescontoEmReais) / 100);
            valorDescontoEmReais = Math.floor(valorReais * 100) / 100;
        }

        const quantidadeFormatada = Number(quantidade)

        if (!loja.parametros.aceita_decimal && !Number.isInteger(quantidadeFormatada)) {
            enqueueSnackbar("Não é permitido valores decimais no campo de quantidades para esse produto", { variant: "error" })
            return
        }

        if (handleVerificarLimiteDesconto()) {
            enqueueSnackbar(`O desconto aplicado excede os ${calcularDescontoMaximo
                }% oferecidos pelo produto`, { variant: "error" })
            return
        }

        onEditarItem({
            ...valueForm,
            desconto: valorDescontoEmReais,
            quantidade: quantidadeFormatada
        })
        onClose()
    }, [onClose, onEditarItem, valueForm, handleVerificarLimiteDesconto, quantidade, calcularDescontoMaximo])

    const valorDescontoEquivalente = useMemo(() => {
        const descontoNumerico = Number(valueForm.desconto);

        if (valueForm.tipoDesconto === "%") {
            const valorEmReais = (item.preco * descontoNumerico) / 100;
            const valorFormatado = Math.floor(valorEmReais * 100) / 100;
            return `${formatarValorMonetario(valorFormatado)} por unidade`;
        } else {
            const percentual = item.preco > 0 ? (descontoNumerico / item.preco) * 100 : 0;
            const percentualFormatado = Math.floor(percentual * 100) / 100;
            return `${percentualFormatado.toFixed(2)}% por unidade`;
        }
    }, [valueForm.desconto, valueForm.tipoDesconto, item.preco]);

    return (
        <Dialog
            open={open}
            onClose={onClose}
            fullWidth
            maxWidth="xs"
            PaperComponent={PaperDraggable}
            aria-labelledby="draggable-dialog-title"
        >
            <DialogTitle style={{ cursor: 'move' }} id="draggable-dialog-title">Editar item do carrinho</DialogTitle>
            <DialogContent>
                <Box borderRadius={1}>
                    <Grid container spacing={1}>
                        <Grid size={{ xs: 12 }}>
                            <Stack direction="row" justifyContent="space-between" alignItems="center">
                                <DadosItem titulo="Produto" valor={item.nome} />
                                <DadosItem titulo="Valor unit." valor={formatarValorMonetario(item.preco)} />
                            </Stack>
                        </Grid>

                    </Grid>
                </Box>
                <Divider sx={{ my: 2 }} />
                <Grid container spacing={2}>
                    <Grid size={{ xs: 12, md: 6 }} alignSelf="center">
                        <Typography><b>{item.loja.nome}</b></Typography>
                    </Grid>

                    <Grid size={{ xs: 12, md: 6 }}>
                        <QuantityInput
                            justifyContent="flex-end"
                            value={quantidade}
                            onChange={(e) => setQuantidade(e)}
                            onClickCapture={(e) => e.stopPropagation()}
                        />
                    </Grid>

                    <Grid container size={{ xs: 12 }} spacing={2} alignItems="center">
                        <Grid size={{ xs: 12 }}>
                            {
                                !permiteDesconto &&
                                <Alert severity="warning">
                                    O desconto não é permitido para esse produto
                                </Alert>
                            }
                            {
                                permiteDesconto &&
                                <Alert severity="info">
                                    O desconto máximo para esse produto é de {calcularDescontoMaximo}%
                                </Alert>
                            }
                        </Grid>

                        <Grid size={{ xs: 4 }}>
                            <FormControl fullWidth disabled={!permiteDesconto}>
                                <Stack direction="row" alignItems="center" justifyContent="flex-start">
                                    <Typography sx={{ color: !permiteDesconto ? "text.disabled" : "text.primary" }}>R$</Typography>
                                    <Switch
                                        checked={valueForm.tipoDesconto === "%"}
                                        onChange={(e) =>
                                            handleChange("tipoDesconto", e.target.checked ? "%" : "R$")
                                        }
                                        slotProps={{ input: { 'aria-label': 'switch' } }}
                                    />
                                    <Typography sx={{ color: !permiteDesconto ? "text.disabled" : "text.primary" }}>%</Typography>
                                </Stack>
                            </FormControl>
                        </Grid>

                        <Grid size={{ xs: 8 }}>
                            <TextField
                                value={descontoDisplay}
                                disabled={!permiteDesconto}
                                fullWidth
                                id="outlined-basic"
                                label={`Desconto unit. (${valueForm.tipoDesconto})`}
                                variant="outlined"
                                type={valueForm.tipoDesconto === "R$" ? "text" : "number"}
                                onChange={(e) => {
                                    const value = e.target.value;
                                    if (valueForm.tipoDesconto === "R$") {
                                        const numericValue = value.replace(/[^\d]/g, '');
                                        handleChange("desconto", numericValue);
                                    } else {
                                        if (value === '' || /^\d*([.,]\d{0,2})?$/.test(value)) {
                                            handleChange("desconto", value);
                                        }
                                    }
                                }}
                                slotProps={{
                                    htmlInput: {
                                        ...(valueForm.tipoDesconto === "%" && { step: 0.01, min: 0, max: 100 })
                                    }
                                }}
                            />
                        </Grid>
                        <Grid size={{ xs: 12 }}>
                            <Typography variant="caption" color="textSecondary">
                                Equivalente: {valorDescontoEquivalente}
                            </Typography>
                        </Grid>
                    </Grid>
                </Grid>
            </DialogContent>

            <DialogActions>
                <Button
                    variant="outlined"
                    onClick={onClose}
                >
                    Cancelar
                </Button>

                <Button
                    variant="contained"
                    onClick={handleConfirmValues}
                    autoFocus
                    disabled={parseInt(quantidade) > item.loja.estoque}
                >
                    Editar
                </Button>
            </DialogActions>
        </Dialog>
    )
}

export const DadosItem = ({
    titulo,
    valor,
    align = 'left'
}: DadosItemProps) => {
    return (
        <Stack>
            <Typography fontWeight="bold" variant="subtitle2" textAlign={align}>{titulo}</Typography>
            <Typography variant="subtitle2" textAlign={align}>{valor}</Typography>
        </Stack>
    )
}

interface DadosItemProps {
    titulo: string;
    valor: string;
    align?: 'left' | 'right';
}

interface DialogEditItemProps {
    open: boolean;
    onClose: () => void;
    item: ICarrinhoItem;
    onEditarItem: (novoItem: ICarrinhoItem) => void;
}
